import React, { useState, useRef } from 'react';
import InlineHelpText from '../../shared/InlineHelpText';
import HelpIcon from '../../shared/HelpIcon';
import { FormattedMessage } from 'react-intl';
import SvgExclamationPointIcon from '../../svg/ExclamationPointIcon';
import { Link } from 'react-router-dom';
import { timeConvert } from '../../../utilities/general';
import AccessControl from '../../../AccessControl';
import permissions from '../../../permissionsList';
import SvgOpenExternal from '../../svg/OpenExternal';
import {
  descriptionText,
  typeText,
} from '../../shared/SharedInternationalizations';
import { connect } from 'react-redux';
import Table from '../../shared/table/Table';

export function UnconnectedUnitCheckinPreferences(props) {
  const {
    unit,
    receive_checkin_reminder,
    call_for_checkin,
    call_for_checkin_time,
    checks_in_children,
    checkin_modes,
    checkin_device,
  } = props.checkInPreferences;
  const {
    currentLocation,
    checkinModes,
    alertDeviceTypes,
    unitObj,
    checkinCall = 15,
  } = props;
  let checkInCallTime;
  if (call_for_checkin_time) {
    checkInCallTime = timeConvert(call_for_checkin_time);
  }

  const [
    showCallUnitCheckInHelpText,
    setShowCallUnitCheckInHelpText,
  ] = useState(false);
  function toggleCallUnitCheckInHelpText(value) {
    setShowCallUnitCheckInHelpText(value);
  }
  const callUnitCheckInHelpTextRef = useRef();
  const callUnitCheckInHelpText = (
    <FormattedMessage
      id="unitCheckinPreferences.callUnitCheckInHelpText"
      defaultMessage="This unit should be called <b>15</b> minutes before the end of the check-in period to check them in if they have not already checked in by that time."
      description="Help text for understanding what the call unit for check in means."
      values={{ b: () => checkinCall }}
    />
  );

  const [showCheckInTimeHelpText, setShowCheckInTimeHelpText] = useState(false);
  function toggleCheckInTimeHelpText(value) {
    setShowCheckInTimeHelpText(value);
  }
  const checkInTimeHelpTextRef = useRef();
  const checkInTimeHelpText = (
    <FormattedMessage
      id="unitCheckinPreferences.checkInTimeHelpText"
      defaultMessage="This is the time to call this unit to check them in."
      description="Help text for understanding what the check-in modes are."
    />
  );

  const [showCheckInModesHelpText, setShowCheckInModesHelpText] = useState(
    false
  );
  function toggleCheckInModesHelpText(value) {
    setShowCheckInModesHelpText(value);
  }
  const checkInModesHelpTextRef = useRef();
  const checkInModesHelpText = (
    <FormattedMessage
      id="unitCheckinPreferences.checkInModesHelpText"
      defaultMessage="These are the check-in modes for this unit."
      description="Help text for understanding what the check-in modes mean."
    />
  );
  const [
    showChecksInChildrenHelpText,
    setShowChecksInChildrenHelpText,
  ] = useState(false);
  function toggleChecksInChildrenHelpText(value) {
    setShowChecksInChildrenHelpText(value);
  }
  const checksInChildrenHelpTextRef = useRef();
  const checksInChildrenHelpText = (
    <FormattedMessage
      id="unitCheckinPreferences.checksInChildrenHelpText"
      defaultMessage="Checking in this unit will check in any child units as well."
      description="Help text for understanding what the check-in modes mean."
    />
  );

  const [showCheckinDeviceHelpText, setShowCheckinDeviceHelpText] = useState(
    false
  );
  function toggleCheckinDeviceHelpText(value) {
    setShowCheckinDeviceHelpText(value);
  }
  const checkinDeviceHelpTextRef = useRef();
  const checkinDeviceHelpText = (
    <FormattedMessage
      id="unitCheckinPreferences.checkinDeviceHelpText"
      defaultMessage="When a unit needs to be called to check in, this device will be what SARA initiates the call to."
      description="Help text for understanding what the check-in modes are."
    />
  );

  const checkInReminder = receive_checkin_reminder ? (
    <div className="content-display__column">
      <div className="content-display__item-pill-container">
        <div className="item-container__info-container">
          <SvgExclamationPointIcon className="text-alert" />
        </div>
        <div className="item-container__main-container">
          <div className="text-large text-light text-alert">
            <FormattedMessage
              data-test="checkin-reminder-message"
              id="unitCheckinPreferences.callUnitCheckInText"
              defaultMessage="Call this unit <b>minutes</b> prior to the end of the check-in period if no check-in has been received"
              description="The check-in instructions"
              values={{
                b: (msg) => (
                  <span
                    className="item-container__text-highlighted item-container__text-highlighted--alert"
                    data-test="checkin-reminder-message-text">
                    {checkinCall} {msg}
                  </span>
                ),
              }}
            />
          </div>
        </div>

        <div className="item-container__info-container">
          <HelpIcon
            styles="margin-left-5"
            onClick={() => callUnitCheckInHelpTextRef.current.toggleHelpText()}
            toggled={showCallUnitCheckInHelpText}
          />
        </div>
      </div>
      <InlineHelpText
        ref={callUnitCheckInHelpTextRef}
        toggleFunction={toggleCallUnitCheckInHelpText}
        message={callUnitCheckInHelpText}
      />
    </div>
  ) : (
    ''
  );
  const checksInChildren = (
    <div className="content-display__column">
      <div className="content-display__item-pill-container">
        <div className="item-container__info-container">
          <SvgExclamationPointIcon className="text-alert" />
        </div>
        <div className="item-container__main-container">
          <div className="item-container__main-text text-large text-light">
            {checks_in_children ? (
              <FormattedMessage
                data-test="checks-in-children-message"
                id="unitCheckinPreferences.unitChildCheckInText"
                defaultMessage="Checking in this unit <b>will</b> check in any child units as well"
                description="The check-in instructions"
                values={{
                  b: (msg) => (
                    <span
                      className="item-container__text-highlighted"
                      data-test="checks-in-children-true-text">
                      {msg}
                    </span>
                  ),
                }}
              />
            ) : (
              <FormattedMessage
                data-test="checks-in-children-message"
                id="unitCheckinPreferences.unitChildFalseCheckInText"
                defaultMessage="Checking in this unit <b>will not</b> check in any child units as well"
                description="The check-in instructions"
                values={{
                  b: (msg) => (
                    <span
                      className="item-container__text-highlighted item-container__text-highlighted--alert"
                      data-test="checks-in-children-false-text">
                      {msg}
                    </span>
                  ),
                }}
              />
            )}
          </div>
        </div>

        <div className="item-container__info-container">
          <HelpIcon
            styles="margin-left-5"
            onClick={() => checksInChildrenHelpTextRef.current.toggleHelpText()}
            toggled={showChecksInChildrenHelpText}
          />
        </div>
      </div>
      <InlineHelpText
        ref={checksInChildrenHelpTextRef}
        toggleFunction={toggleChecksInChildrenHelpText}
        message={checksInChildrenHelpText}
      />
    </div>
  );

  const checkinTime = call_for_checkin ? (
    <div
      className="content-display__column content-display__column--1-2"
      data-test="call-for-checkin-container">
      <div className="content-display__item-pill-container">
        <div className="item-container__main-container">
          <div className="item-container__main-text text-large text-light">
            <FormattedMessage
              data-test="call-for-checkin"
              id="unitCheckinPreferences.checkInTimeText"
              defaultMessage="Call at <b>a</b> to check in this unit"
              description="The check-in instructions"
              values={{
                b: (msg) => (
                  <span
                    className="item-container__text-highlighted"
                    data-test="call-for-checkin-text">
                    {checkInCallTime}
                  </span>
                ),
              }}
            />
          </div>
        </div>
        <div className="item-container__info-container">
          <HelpIcon
            styles="margin-left-5"
            onClick={() => checkInTimeHelpTextRef.current.toggleHelpText()}
            toggled={showCheckInTimeHelpText}
          />
        </div>
      </div>
      <InlineHelpText
        ref={checkInTimeHelpTextRef}
        toggleFunction={toggleCheckInTimeHelpText}
        message={checkInTimeHelpText}
      />
    </div>
  ) : (
    ''
  );

  const checkinModesDisplay =
    checkin_modes.length > 0 ? (
      <div
        className="content-display__column content-display__column--1-2"
        data-test="checkin-modes-container">
        <div className="content-display__item-pill-container">
          <div className="item-container__main-container">
            <div className="item-container__main-text text-large text-light">
              <FormattedMessage
                id="unitCheckinPreferences.checkInModesText"
                defaultMessage="Check-in Modes"
                description="The check-in instructions"
                data-test="checkin-modes"
              />
            </div>
            <div
              className="item-container__secondary-text text-medium text-bold"
              data-test="checkin-modes-list">
              {checkin_modes.map((mode, index) => (
                <div data-test="checkin-mode" className="" key={index}>
                  {checkinModes[mode].mode_name}
                </div>
              ))}
            </div>
          </div>
          <div className="item-container__info-container">
            <HelpIcon
              styles="margin-left-5"
              onClick={() => checkInModesHelpTextRef.current.toggleHelpText()}
              toggled={showCheckInModesHelpText}
            />
          </div>
        </div>
        <InlineHelpText
          ref={checkInModesHelpTextRef}
          toggleFunction={toggleCheckInModesHelpText}
          message={checkInModesHelpText}
        />
      </div>
    ) : (
      ''
    );

  /**
   * Function to generate the units table
   * @returns {object} - The table of units
   */
  const alertDevicesTable = () => {
    const alertDeviceDetailsList = [];
    const allDevices = [
      ...(unitObj.available_checkin_devices || []),
      ...(unitObj.alert_devices || []),
    ];
    const seen = new Set();
    const deduped = allDevices.filter((d) => {
      if (seen.has(d.alert_device_id)) return false;
      seen.add(d.alert_device_id);
      return true;
    });
    const alertDeviceList = deduped.filter(
      (d) => d.alert_device_id === checkin_device
    );
    alertDeviceList.map((details) => {
      let deviceLink = (
        <AccessControl
          data-test="access-control"
          feature={permissions.static.devices.alert.editAlertDevice}>
          <FormattedMessage
            id="unitDisplay.viewAlertDeviceText"
            defaultMessage="View Device"
            description="Button to view full unit info">
            {(txt) => (
              <div className="width-100 flex-end height-100 align-center">
                <a
                  data-test="view-alert-device-link"
                  className="text-small margin-right-10 link-text align-center"
                  href={`/admin/alert_dev_edit.php?&alert_device_options=&mode=edit&edit_row[alert_device_id]=${details.alert_device_id}&edit_row[alert_device_type]=${details.alert_device_type}`}>
                  {txt} <SvgOpenExternal className="margin-left-5" />
                </a>
              </div>
            )}
          </FormattedMessage>
        </AccessControl>
      );
      if (
        details.alert_device_type === 3 ||
        details.alert_device_type === 4 ||
        details.alert_device_type === 24 ||
        details.alert_device_type === 25
      ) {
        deviceLink = (
          <AccessControl
            data-test="access-control"
            feature={permissions.static.devices.alert.editAlertDevice}>
            <FormattedMessage
              id="unitDisplay.viewAlertDevice"
              defaultMessage="View Device"
              description="Button to view full unit info">
              {(txt) => (
                <div className="width-100 flex-end height-100 align-center">
                  <Link
                    data-test="view-alert-device-link-typed"
                    to={`/devices/alert/${details.alert_device_id}/view/`}
                    state={{
                      previous: currentLocation,
                    }}
                    className="text-small margin-right-10 link-text align-center">
                    {txt} <SvgOpenExternal className="margin-left-5" />
                  </Link>
                </div>
              )}
            </FormattedMessage>
          </AccessControl>
        );
      }

      alertDeviceDetailsList.push({
        id: details.alert_device_id,
        description: details.alert_description,
        type: details.alert_device_type,
        view: deviceLink,
      });

      return '';
    });

    // map over the units and put into the format that is required for the table component
    if (alertDeviceDetailsList.length > 0) {
      const devicesArray = alertDeviceDetailsList.map((d) => {
        let deviceType = '';
        if (alertDeviceTypes[d.type]) {
          deviceType = alertDeviceTypes[d.type].description;
        }
        return {
          description: {
            sortBy: d.description.toUpperCase(),
            display: d.description,
          },
          type: {
            sortBy: deviceType.toUpperCase(),
            display: deviceType,
          },
          view: {
            sortBy: '',
            display: d.view,
          },
        };
      });
      // Take the array and filter based on the text entered in the search bar.

      const tableHeadings = [
        {
          isSortable: true,
          sortBy: 'description',
          display: descriptionText,
        },
        {
          isSortable: true,
          sortBy: 'type',
          display: typeText,
        },
        {
          isSortable: false,
          sortBy: 'view',
          display: '',
        },
      ];

      // generates the actual table
      const table = (
        <Table
          hasTableHead={false}
          hasSelector={false}
          tableHeadings={tableHeadings}
          sortParam="description"
          tableData={devicesArray}
          tableStyles="table-layout__3-column"
          data-test="checkin-device-table"
        />
      );
      return (
        <div
          className="content-display__column"
          data-test="alert-devices-container">
          <div className="content-display__item-pill-container">
            <div className="item-container__main-container">
              <div className="item-container__main-text text-large text-light flex-spread margin-bottom-10">
                <div className="align-center">
                  <FormattedMessage
                    id="unitDevices.checkinDeviceText"
                    defaultMessage="Check-in Device:"
                    description="The assigned alert devices for this unit"
                  />
                  <HelpIcon
                    onClick={() =>
                      checkinDeviceHelpTextRef.current.toggleHelpText()
                    }
                    toggled={showCheckinDeviceHelpText}
                    styles="margin-left-5"
                  />
                </div>
              </div>
              <div className="item-container__secondary-text text-medium text-bold flex-row align-center">
                {table}
              </div>
            </div>
            <div className="item-container__info-container"></div>
          </div>
          <InlineHelpText
            ref={checkinDeviceHelpTextRef}
            isToggled={toggleCheckinDeviceHelpText}
            message={checkinDeviceHelpText}
          />
        </div>
      );
    } else {
      return '';
    }
  };

  return (
    <div className="content-display__container" data-test="content-container">
      <div className="width-100 flex-spread align-center">
        <FormattedMessage
          id="unitCheckinPreferences.unitCheckInOptions"
          defaultMessage="Unit Check-In Options"
          description="The check-in options">
          {(txt) => (
            <span className="text-large text-light no-margin">{txt}: </span>
          )}
        </FormattedMessage>
      </div>
      <React.Fragment>
        {checkInReminder}
        {checkinTime}
        {checkinModesDisplay}
        {checksInChildren}
        {alertDevicesTable()}
        <div className="width-100 flex-spread align-center">
          <div></div>
          <AccessControl
            data-test="access-control"
            feature={permissions.static.units.editUnitCheckinProfile}>
            {unitObj.is_active ? (
              <FormattedMessage
                id="unitCheckinPreferences.unitCheckInOptionsEdit"
                defaultMessage="Edit Check-In Options"
                data-test="edit-link-text"
                description="The check-in options">
                {(txt) => (
                  <Link
                    data-test="edit-link"
                    to={`/units/${unit}/edit/?mode=checkInPreferences`}
                    state={{
                      modal: true,
                      previous: currentLocation,
                    }}
                    className="link-button button-secondary button-sm text-small">
                    {txt}
                  </Link>
                )}
              </FormattedMessage>
            ) : (
              ''
            )}
          </AccessControl>
        </div>
      </React.Fragment>
    </div>
  );
}

const mapStateToProps = (state, ownProps) => {
  return {
    alertDeviceTypes: state.devices.alertDeviceTypes,
    checkinCall: state.system.siteInfo.checkin_reminder_time,
  };
};

export default connect(mapStateToProps)(UnconnectedUnitCheckinPreferences);
